import { cn } from "@/utils/cn";
import { useDrawerA11y, useInert } from "@/hooks/useDrawerA11y";
import { useIsMobileViewport } from "@/hooks/useIsMobileViewport";
import { CSSProperties, ReactNode, useCallback, useId, useRef, useState } from "react";

const DEFAULT_DESKTOP_WIDTH = "14rem";
const DEFAULT_MOBILE_WIDTH = "18rem";
const DEFAULT_MIN_WIDTH = 280;
const DEFAULT_MAX_WIDTH = 800;

export const PANEL_SLIDE_SPEED = "300";

export type SlideablePanelResizability = {
  onWidthChange: (widthPx: number) => void;
  minWidth?: number;
  maxWidth?: number;
  /** Dragging well below minWidth calls this, such as to hide the panel. */
  onCollapse?: () => void;
};

export type SlideablePanelProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  offsetTop?: string;
  from: "left" | "right";
  width?: string;
  alwaysOpenOnDesktop: boolean;
  ariaLabel?: string;
  /** Stable id for aria-controls (e.g. NavBar toggle). Falls back to useId(). */
  id?: string;
  /** Merged onto the panel `<aside>` (e.g. docs chrome overrides). */
  panelClassName?: string;
  resizability?: SlideablePanelResizability;
};

const parseWidthPx = (width: string | undefined): number => {
  if (!width) return DEFAULT_MIN_WIDTH;
  const parsed = Number.parseFloat(width);
  return Number.isFinite(parsed) ? parsed : DEFAULT_MIN_WIDTH;
};

export const SlideablePanel = ({
  children,
  isOpen,
  onClose,
  offsetTop = "0",
  from = "left",
  width,
  alwaysOpenOnDesktop,
  ariaLabel = "Panel",
  id,
  panelClassName,
  resizability,
}: SlideablePanelProps) => {
  const isMobile = useIsMobileViewport();
  const panelRef = useRef<HTMLElement>(null);
  const generatedId = useId().replace(/:/g, "");
  const panelId = id ?? generatedId;
  const [dragging, setDragging] = useState(false);

  useDrawerA11y({
    isOpen,
    onClose,
    containerRef: panelRef,
    enabled: isMobile && isOpen,
  });

  useInert(panelRef, !isOpen && (isMobile || !alwaysOpenOnDesktop));
  const desktopWidth = width ?? DEFAULT_DESKTOP_WIDTH;
  // alwaysOpenOnDesktop left nav keeps a fixed mobile width (PageLayout UX parity)
  const mobileWidth = alwaysOpenOnDesktop
    ? DEFAULT_MOBILE_WIDTH
    : (width ?? DEFAULT_MOBILE_WIDTH);

  const panelStyle = {
    "--panel-width": desktopWidth,
    "--panel-mobile-width": mobileWidth,
  } as CSSProperties;

  const desktopPosition =
    from === "left"
      ? "lg:relative lg:top-auto lg:bottom-auto lg:left-auto lg:z-10 lg:shadow-none"
      : "lg:relative lg:top-auto lg:bottom-auto lg:right-auto lg:z-10 lg:shadow-none";

  // Inner edge opposite the slide-in side
  const handleEdge = from === "right" ? "left" : "right";
  const isResizable = !!resizability;
  const showResizeHandle = isResizable && !isMobile;

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (!resizability) return;
      e.preventDefault();

      const startX = e.clientX;
      const startWidth = parseWidthPx(width);
      const minWidth = resizability.minWidth ?? DEFAULT_MIN_WIDTH;
      const maxWidth = resizability.maxWidth ?? DEFAULT_MAX_WIDTH;
      const { onWidthChange, onCollapse } = resizability;

      setDragging(true);

      // Cover the whole window (including preview iframes) while dragging so
      // mousemove/mouseup aren't swallowed by embedded content.
      const overlay = document.createElement("div");
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;cursor:col-resize";
      document.body.appendChild(overlay);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMove = (ev: MouseEvent) => {
        const delta =
          handleEdge === "left" ? startX - ev.clientX : ev.clientX - startX;
        if (onCollapse && startWidth + delta < minWidth * 0.6) {
          onUp();
          onCollapse();
          return;
        }
        onWidthChange(Math.max(minWidth, Math.min(maxWidth, startWidth + delta)));
      };

      function onUp() {
        setDragging(false);
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        overlay.remove();
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [resizability, width, handleEdge],
  );

  return (
    <div
      className="slideable-panel flex relative h-full"
      style={panelStyle}
    >
      <div
        aria-hidden
        className={cn(
          "fixed inset-x-0 bottom-0 z-[41] bg-zinc-900/45 transition-opacity duration-300 lg:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        style={{ top: offsetTop }}
        onClick={onClose}
      />
      <div
        className={cn(
          "max-lg:contents lg:shrink-0",
          // Clip when collapsed; when open+resizable allow the handle to extend 3px past the border.
          // alwaysOpenOnDesktop is never collapsed on lg+, so only clip if not resizable.
          (alwaysOpenOnDesktop
            ? !isResizable
            : !isOpen || !isResizable) && "lg:overflow-hidden",
          alwaysOpenOnDesktop
            ? "lg:w-[var(--panel-width)]"
            : cn(
              !dragging &&
                `motion-safe:lg:transition-[width] motion-safe:lg:duration-${PANEL_SLIDE_SPEED} motion-safe:lg:ease-out`,
              isOpen ? "lg:w-[var(--panel-width)]" : "lg:w-0",
            ),
        )}
      >
        <aside
          ref={panelRef}
          id={panelId}
          role={isMobile && isOpen ? "dialog" : undefined}
          aria-modal={isMobile && isOpen ? true : undefined}
          aria-label={isMobile && isOpen ? ariaLabel : undefined}
          aria-hidden={!isOpen && (isMobile || !alwaysOpenOnDesktop) ? true : undefined}
          tabIndex={-1}
          className={cn(
            "flex h-[calc(100dvh-3.5rem)] flex-col overscroll-contain bg-white lg:h-full",
            // Scroll on an inner wrapper so the resize handle can extend past the border
            !isResizable && "overflow-y-auto",
            `motion-safe:transition-transform motion-safe:duration-${PANEL_SLIDE_SPEED} motion-safe:ease-out`,
            "fixed bottom-0 top-14 z-[43] w-[min(var(--panel-mobile-width),88vw)] lg:w-[var(--panel-width)]",
            from === "left"
              ? "left-0 max-lg:shadow-[4px_0_24px_rgba(0,0,0,0.08)]"
              : "right-0 max-lg:shadow-[-4px_0_24px_rgba(0,0,0,0.08)]",
            alwaysOpenOnDesktop
              ? isOpen
                ? "max-lg:translate-x-0"
                : from === "left"
                  ? "max-lg:-translate-x-full"
                  : "max-lg:translate-x-full"
              : isOpen
                ? "translate-x-0"
                : from === "left"
                  ? "-translate-x-full"
                  : "translate-x-full",
            alwaysOpenOnDesktop
              ? cn(desktopPosition, "lg:translate-x-0")
              : desktopPosition,
            // Don't add bare `relative` — it overrides `fixed` on mobile and collapses the main column.
            // Desktop already gets `lg:relative` from desktopPosition.
            isResizable && "lg:overflow-visible",
            isResizable && (handleEdge === "left" ? "border-l border-zinc-200/70" : "border-r border-zinc-200/70"),
            panelClassName,
          )}
        >
          {showResizeHandle && (
            <div
              aria-hidden
              tabIndex={-1}
              onMouseDown={handleResizeStart}
              className={cn(
                // 5px = 2px outside + 1px border + 2px inside (absolute edges are the padding box)
                "absolute top-0 bottom-0 w-[5px] cursor-col-resize z-10 hover:bg-zinc-300/40 transition-colors",
                handleEdge === "left" ? "-left-[3px]" : "-right-[3px]",
                dragging && "bg-zinc-300/50",
              )}
            />
          )}
          {isResizable ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
              {children}
            </div>
          ) : (
            children
          )}
        </aside>
      </div>
    </div>
  );
};
